<template>
    <common-modal width="pricing" @close="emitEvent({ name: 'close' })">
        <div v-if="isUnbilled" class="billing-default-plan">
            <p class="billing-default-plan--info">Network (free): your practice refers patients out and is not billed.</p>
        </div>
        <div v-else-if="showPricingDetails && !skipPlanSelection && !isLegacyBilling" class="pricing-modal-plans">
            <pricing-cards @select-plan="onSelectPlan" @upgrade="onSelectPlan"></pricing-cards>
        </div>
        <div v-else-if="isFreeCheckout" class="billing-default-plan">
            <p class="billing-default-plan--info">{{ $t('pages.practice.billing.free_plan_confirm') }}</p>
            <common-buttons align="center">
                <common-button
                    :isProcessing="isSending"
                    :label="$t('pages.practice.billing.join_free')"
                    type="primary"
                    size="large"
                    @click="subscribeFree"
                ></common-button>
            </common-buttons>
        </div>
        <div v-else class="billing-information">
            <div v-if="!skipPlanSelection" class="billing-information--side">
                <common-content-spacer height="micro">
                    <p>{{ $t('pages.practice.billing.plan_type') }}</p>
                    <div class="billing-information--side--plan">
                        <common-page-header
                            :heading="isLegacyBilling ? 'Per-location subscription' : selectedPlanName"
                            class="referral-details__heading"
                            size="large"
                        ></common-page-header>
                        <div>
                            <p v-if="isAnnual && yearlyPrice != null" class="billing-form--info">
                                <span>${{ yearlyPrice }}</span>
                                {{ $t('pages.billing.billing.billing_form.year') }}
                            </p>
                            <p v-else-if="monthlyPrice != null" class="billing-form--info">
                                <span>${{ monthlyPrice }}</span>
                                {{ $t('pages.billing.billing.billing_form.month') }}
                            </p>
                        </div>
                    </div>
                    <div v-if="savingsAmount > 0" class="billing-information--side--price__save">
                        <div class="billing-information--side--price__save--badge">
                            <common-icon
                                name="member-transparent"
                                size="small"
                                class="billing-information--side--price__save--icon"
                            ></common-icon>
                            <div class="billing-information--side--price__save--content">
                                <span class="billing-information--side--price__save--label">
                                    {{ $t('pages.practice.billing.you_save') }}
                                </span>
                                <span class="billing-information--side--price__save--amount">${{ savingsAmount }}</span>
                                <span class="billing-information--side--price__save--annual">
                                    {{ $t('pages.billing.billing.on_annual') }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="billing-information--side--price">
                        <div class="billing-form-type">
                            <span>{{ $t('pages.billing.billing.billing_form.monthly') }}</span>
                            <switch-input
                                :value="isAnnual"
                                spacing="space-between"
                                :showBlueCircle="true"
                                @change="changeIsAnnual"
                            ></switch-input>
                            <span>{{ $t('pages.billing.billing.billing_form.annual') }}</span>
                        </div>
                    </div>
                    <common-tabs :tabs="tabs" :activeTab="tabs[0].name">
                        <template v-slot:features>
                            <billing-features-list :featuresList="featuresList" :isHeaderCenter="false"></billing-features-list>
                        </template>
                        <template v-slot:details>
                            <p v-if="isLegacyBilling">Billed per location, plus a fee per appointment scheduled.</p>
                            <price-explaination v-else :plan="selectedPlan"></price-explaination>
                        </template>
                    </common-tabs>
                </common-content-spacer>
            </div>
            <div class="billing-information--main">
                <div v-if="shouldShowCardList" class="cards-container">
                    <common-page-header
                        heading="Saved payment methods"
                        class="referral-details__heading"
                        contentSpacing="medium"
                        size="page-plain-header"
                    ></common-page-header>
                    <div class="saved-cards__carousel">
                        <div
                            v-for="card in cardList"
                            :key="card.id"
                            :class="getCardItemClass(card)"
                            @click="selectedCardId = card.id"
                        >
                            <div class="saved-cards__content">
                                <span class="saved-cards__brand">{{ getCardBrandName(card.card.brand) }}</span>
                                <span class="saved-cards__number">•••• {{ card.card.last4 }}</span>
                            </div>
                            <div :class="getIndicatorClass(card)">
                                <common-icon v-if="selectedCardId === card.id" name="white-check" size="small"></common-icon>
                            </div>
                        </div>
                    </div>
                    <common-buttons align="center">
                        <common-button
                            :isProcessing="isCardPaymentLoading"
                            :label="$t('common.microcopy.continue')"
                            type="primary"
                            size="small"
                            @click="triggerSavedCardSubscription"
                        ></common-button>
                    </common-buttons>
                    <common-divider>
                        <p>{{ $t('pages.auth.signup.steps.register.description.or') }}</p>
                    </common-divider>
                    <p class="saved-cards__payment-heading" @click="showNewCardFrom = true">
                        <common-icon name="referral-add" size="small"></common-icon>
                        Add a new payment method
                    </p>
                </div>
                <billing-payment-form
                    v-if="shouldShowPaymentForm"
                    :monthlyPrice="monthlyPrice"
                    :yearlyPrice="yearlyPrice"
                    :isAlreadySubscribed="isAlreadySubscribed"
                    :isAnnual="isAnnual"
                    :selectedPlan="selectedPlan"
                    @billing-address-saved="saveBillingAddress"
                    @subscription-created="subscriptionCreated"
                ></billing-payment-form>
            </div>
        </div>
    </common-modal>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import BillingFeaturesList from 'components/page-elements/practice/billing/BillingFeaturesList'
    import PricingCards from 'components/page-elements/practice/billing/PricingCards'
    import BillingPaymentForm from 'components/page-elements/practice/billing/BillingPaymentForm'
    import PracticesAPI from 'api/PracticesAPI'
    import SwitchInput from 'components/form-inputs/SwitchInput'
    import PriceExplaination from 'components/page-elements/practice/billing/PriceExplaination'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import { annualSavings, buildPlanFeatureList, getCheckoutPrices, submitPlanCheckout } from 'services/planCheckoutHelpers'
    import { isFreePlanCode, isUnbilledPracticeType, shouldUseSubscribe, usesLegacyBilling } from 'services/billingPlanHelpers'

    export default {
        mixins: [FormMixin],
        components: { BillingFeaturesList, BillingPaymentForm, SwitchInput, PriceExplaination, PricingCards },

        props: {
            isAlreadySubscribed: { type: Boolean, default: false },
            mode: { type: String, default: 'subscription' },
        },

        data() {
            return {
                isSending: false,
                featuresList: [],
                monthlyPrice: null,
                yearlyPrice: null,
                isAnnual: true,
                showPricingDetails: true,
                selectedPlan: null,
                cardList: [],
                selectedCardId: null,
                showNewCardFrom: false,
                isCardPaymentLoading: false,
                isFreeCheckout: false,
            }
        },

        computed: {
            tabs() {
                return [
                    { name: 'features', displayName: this.$t('pages.billing.billing.features') },
                    { name: 'details', displayName: this.$t('pages.billing.billing.billing_details') },
                ]
            },
            skipPlanSelection() {
                return this.mode === 'add-card' || this.isAlreadySubscribed
            },
            isUnbilled() {
                return isUnbilledPracticeType(this.$auth.getPractice()?.practiceType)
            },
            isLegacyBilling() {
                return usesLegacyBilling(this.$auth.getPractice()?.practiceType)
            },
            selectedPlanName() {
                return this.selectedPlan?.name || ''
            },
            savingsAmount() {
                return annualSavings(this.monthlyPrice, this.yearlyPrice)
            },
            shouldShowCardList() {
                if (this.mode === 'add-card') {
                    return false
                }
                return this.cardList.length > 0 && !this.showNewCardFrom
            },
            shouldShowPaymentForm() {
                if (this.mode === 'add-card') {
                    return true
                }
                return this.cardList.length === 0 || this.showNewCardFrom
            },
            getIndicatorClass() {
                return (card) => ({
                    'saved-cards__indicator': true,
                    'saved-cards__indicator--selected': this.selectedCardId === card.id,
                })
            },
            getCardItemClass() {
                return (card) => ({
                    'saved-cards__item': true,
                    'saved-cards__item--selected': this.selectedCardId === card.id,
                })
            },
        },

        methods: {
            onSelectPlan(payload) {
                const plan = payload?.plan || payload
                if (!plan?.code) {
                    return
                }
                this.selectedPlan = plan
                if (isFreePlanCode(plan.code)) {
                    if (this.$auth.isPracticePremium()) {
                        this.isSending = true
                        PracticesAPI.downgradeToFree()
                            .then(() => this.subscriptionCreated(plan))
                            .finally(() => {
                                this.isSending = false
                            })
                        return
                    }
                    this.isFreeCheckout = true
                    this.showPricingDetails = false
                    return
                }
                this.isFreeCheckout = false
                this.applyPlanPricing(plan)
                this.featuresList = buildPlanFeatureList(plan, this.$t.bind(this))
                this.showPricingDetails = false
            },

            applyPlanPricing(plan) {
                const prices = getCheckoutPrices(plan)
                this.monthlyPrice = prices.monthlyPrice
                this.yearlyPrice = prices.yearlyPrice
            },

            saveBillingAddress() {
                this.emitEvent({ name: 'close' })
                this.$auth.forceUpdatePractice()
            },

            changeIsAnnual() {
                this.isAnnual = !this.isAnnual
            },

            subscriptionCreated(plan) {
                this.emitEvent({ name: 'subscription-created', payload: plan || this.selectedPlan })
                this.$auth.forceUpdatePractice()
                this.emitEvent({ name: 'close' })
            },

            // Legacy per-location prices for this practice (Core reads them from STRIPE_CONFIG).
            loadLegacyPrices() {
                PracticesAPI.getEstimatedPrice().then((price) => {
                    this.monthlyPrice = price?.monthlyPrice ?? null
                    this.yearlyPrice = price?.yearlyPrice ?? null
                })
            },

            getPracticeCards() {
                PracticesAPI.getPracticeCards().then((response) => {
                    this.cardList = response
                    if (response?.[0]?.id) {
                        this.selectedCardId = response[0].id
                    }
                })
            },

            getCardBrandName(brand) {
                if (brand === 'mastercard') return 'MC'
                return brand.toUpperCase()
            },

            subscribeFree() {
                this.isSending = true
                submitPlanCheckout({
                    practice: this.$auth.getPractice(),
                    plan: this.selectedPlan || { code: PracticePlanEnum.FREE },
                    isAnnual: false,
                })
                    .then(() => this.subscriptionCreated(this.selectedPlan))
                    .finally(() => {
                        this.isSending = false
                    })
            },

            triggerSavedCardSubscription() {
                if (!this.selectedCardId || (!this.selectedPlan && !this.isLegacyBilling)) {
                    return
                }
                this.isCardPaymentLoading = true
                const practice = this.$auth.getPractice()
                const billingAddress = practice.billingAddress

                const request = this.isLegacyBilling
                    ? PracticesAPI.createSubscription({
                          billingAddress,
                          isAnnual: this.isAnnual,
                          paymentMethodId: this.selectedCardId,
                      })
                    : shouldUseSubscribe(practice)
                    ? submitPlanCheckout({
                          practice,
                          plan: this.selectedPlan,
                          isAnnual: this.isAnnual,
                          paymentMethodId: this.selectedCardId,
                          billingAddress,
                      })
                    : PracticesAPI.changePlan({
                          planCode: this.selectedPlan.code,
                          interval: this.isAnnual ? 'ANNUAL' : 'MONTHLY',
                      })

                request
                    .then(() => {
                        this.subscriptionCreated(this.selectedPlan)
                    })
                    .finally(() => {
                        this.isCardPaymentLoading = false
                    })
            },
        },

        mounted() {
            if (this.mode === 'subscription' && !this.isUnbilled) {
                this.getPracticeCards()
            }
            if (this.skipPlanSelection) {
                this.showPricingDetails = false
            }
            if (this.mode === 'subscription' && this.isLegacyBilling && !this.isAlreadySubscribed) {
                this.showPricingDetails = false
                this.loadLegacyPrices()
            }
        },
    }
</script>

<style lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .pricing-modal-plans {
        margin: -8px;
        padding: 8px;
        background: #f7f9fc;
        border-radius: 8px;
    }

    .billing-default-plan {
        padding: 40px 20px;

        &--info {
            font-family: 'Poppins';
            font-style: normal;
            font-weight: 700;
            font-size: 18px;
            line-height: 28px;
            color: $color--txt-base-5;
            text-align: center;
            margin-bottom: 24px;
        }

        &--accept {
            font-family: 'Poppins';
            font-style: normal;
            font-weight: 500;
            font-size: 14px;
            line-height: 28px;
            color: $color--txt-base-5;
            text-align: center;

            .icon {
                margin-left: 15px;
            }
        }
    }
    .billing-form-type {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 12px;
        max-width: 260px;
        .form-field {
            margin: 0 10px;
        }
    }
    .billing-information {
        display: flex;

        @include breakpoint(tablet) {
            display: block;
            max-height: 700px;
        }

        &--side {
            @include breakpoint(tablet) {
                min-width: 100%;
                padding-right: 0;
            }
            background: $color--bg-dashboard;
            border-radius: 8px;
            padding: 10px;
            min-width: 371px;

            .tab-wrapper {
                .tab {
                    @include breakpoint(tablet) {
                        width: 100%;
                    }
                }
            }

            &--plan {
                display: flex;
                justify-content: space-between;
                align-items: center;
                p {
                    font-weight: 600;
                    color: $color-black;
                }
            }

            &--price {
                &__value {
                    font-weight: 600;
                    font-size: 20px;
                    line-height: 30px;
                    letter-spacing: 0.325px;
                    color: $color--accent-3;
                }

                &__save {
                    display: flex;
                    justify-content: flex-end;
                    margin-bottom: 20px;

                    &--badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 10px;
                        padding: 4px 12px;
                        border-radius: 999px;
                        background: linear-gradient(135deg, #ecfdf3 0%, #d1fae5 100%);
                        border: 1px solid #a7f3d0;
                        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.12), 0 2px 6px rgba(16, 185, 129, 0.08);
                    }

                    &--icon {
                        color: #059669;
                        flex-shrink: 0;
                    }

                    &--content {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        flex-wrap: wrap;
                    }

                    &--label {
                        font-size: 12px;
                        font-weight: 500;
                        color: #065f46;
                    }

                    &--amount {
                        font-size: 15px;
                        font-weight: 700;
                        color: #059669;
                    }

                    &--annual {
                        font-size: 12px;
                        font-weight: 600;
                        color: #065f46;
                    }
                }
            }
        }

        &--main {
            @include breakpoint(tablet) {
                padding: 0;
            }

            width: 100%;
            max-height: 100vh;
            overflow-x: hidden;
        }
        .cards-container {
            padding: 10px;
        }
        .saved-cards {
            margin-top: 20px;

            &__carousel {
                display: flex;
                gap: 14px;
                margin-bottom: 20px;
                overflow-x: auto;
                overflow-y: hidden;
                padding: 8px 2px 14px;
                scroll-behavior: smooth;
                scrollbar-width: thin;
                scrollbar-color: $color--accent-1 transparent;

                &::-webkit-scrollbar {
                    height: 7px;
                }

                &::-webkit-scrollbar-track {
                    background: #eef3f7;
                    border-radius: 20px;
                }

                &::-webkit-scrollbar-thumb {
                    background: $color--accent-1;
                    border-radius: 20px;
                }

                &::-webkit-scrollbar-thumb:hover {
                    background: darken($color--accent-1, 8%);
                }
            }

            &__item {
                flex: 0 0 auto;
                min-width: 235px;
                max-width: 235px;
                height: 64px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 0 18px;
                border-radius: 999px;
                border: 2px solid #e8edf2;
                background: #fff;
                cursor: pointer;
                transition: 0.25s ease;

                &:hover {
                    transform: translateY(-2px);
                    border-color: $color--accent-1;
                    box-shadow: 0 8px 22px rgba(19, 179, 227, 0.12);
                }

                &--selected {
                    border-color: $color--accent-1;
                    background: linear-gradient(90deg, rgba(19, 179, 227, 0.06), rgba(19, 179, 227, 0.01));
                    box-shadow: 0 10px 24px rgba(19, 179, 227, 0.15);
                }
            }

            &__content {
                display: flex;
                align-items: center;
                gap: 12px;
            }

            &__brand {
                color: #1557c0;
                font-size: 16px;
                font-weight: 700;
            }

            &__number {
                font-size: 15px;
                color: $color--txt-base-5;
            }

            &__indicator {
                width: 28px;
                height: 28px;
                border-radius: 50%;
                border: 2px solid #d9e2ec;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: 0.2s ease;

                &--selected {
                    background: $color--accent-1;
                    border-color: $color--accent-1;
                }
            }
            &__payment-heading {
                color: $color--accent-1;
                cursor: pointer;
                font-weight: 500;
                .icon {
                    margin-right: 6px;
                }
            }
        }
    }
</style>
